import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Alert,
} from '@mui/material'
import { AutoCoderFramework } from '../../types/gotypes'
import MatchEvidence from './MatchEvidence'
import AutoCoderStatusChip from './AutoCoderStatusChip'
import { RecordCodingBlock, RecordRow, normalizeRecordCodings } from '../../utils/recordCodings'

interface ExpandedMatchDetailsModalProps {
  open: boolean
  record: RecordRow | null
  frameworks: AutoCoderFramework[]
  /** pipelineId -> frameworks, for a record coded by several pipelines. */
  frameworksMap?: Record<string, AutoCoderFramework[]>
  colorMap?: Record<string, string>
  onClose: () => void
}

interface MatchRow {
  variable: string
  code: string
  explanation: string
  type: 'main' | 'looped'
}

const matchRows = (block: RecordCodingBlock): MatchRow[] => {
  const rows: MatchRow[] = []
  block.matches.forEach(match => {
    // The main match (theme)
    rows.push({
      variable: match.root_match?.name || 'theme',
      code: match.root_match?.code || '',
      explanation: match.root_match?.explanation || '',
      type: 'main',
    })
    // The looped matches (sentiment, emotion)
    match.looped_matches?.forEach(loopedMatch => {
      rows.push({
        variable: loopedMatch.name || '',
        code: loopedMatch.code || '',
        explanation: loopedMatch.explanation || '',
        type: 'looped',
      })
    })
  })
  return rows
}

/**
 * The full reading of one record: its source text under EVERY coding it has,
 * grouped by the pipeline that produced each — the same stack the table shows,
 * with each coding's matches spelled out underneath it.
 */
const ExpandedMatchDetailsModal: React.FC<ExpandedMatchDetailsModalProps> = ({
  open,
  record,
  frameworks,
  frameworksMap,
  colorMap,
  onClose,
}) => {
  if (!record || !record.data) {
    return null
  }

  const normalized = normalizeRecordCodings(record)

  const frameworksForBlock = (block: RecordCodingBlock): AutoCoderFramework[] => {
    if (frameworksMap && block.pipelineId && frameworksMap[block.pipelineId]) {
      return frameworksMap[block.pipelineId]
    }
    return frameworks || []
  }

  const renderBlock = (block: RecordCodingBlock) => {
    const rows = matchRows(block)
    return (
      <Box key={block.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(block.label || !block.isCoded) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {block.label && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {block.label}
              </Typography>
            )}
            {!block.isCoded && (
              <AutoCoderStatusChip state={block.state} error={block.error} />
            )}
          </Box>
        )}
        {block.error && (
          <Alert severity="error">
            <Typography variant="body2">{block.error}</Typography>
          </Alert>
        )}
        {block.isCoded && (
          <>
            <Typography variant="h6">Source Text</Typography>
            <Paper elevation={1} sx={{ p: 2 }}>
              <MatchEvidence
                record={{
                  interview_id: normalized.interviewId,
                  source_text: normalized.sourceText,
                  matches: [],
                  suggestions: [],
                }}
                matches={block.matches}
                frameworks={frameworksForBlock(block)}
                colorMap={colorMap}
                showLoopedVariables={true}
              />
            </Paper>
            <Typography variant="h6">All Matches</Typography>
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '150px', fontWeight: 'bold' }}>Variable</TableCell>
                    <TableCell sx={{ width: '200px', fontWeight: 'bold' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Explanation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No matches found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow
                        key={`${block.key}-${row.variable}-${row.code}-${index}`}
                        sx={{
                          backgroundColor: row.type === 'main' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: row.type === 'main' ? 600 : 400 }}
                          >
                            {row.variable}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.explanation}</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle>
        Match Details - Interview {normalized.interviewId}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Record-level error (legacy single-pipeline readers still set it) */}
        {record.state === 'failed' && record.error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Error
            </Typography>
            <Typography variant="body2">
              {record.error}
            </Typography>
          </Alert>
        )}

        {normalized.blocks.length === 0 ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              Source Text
            </Typography>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {normalized.sourceText || 'No source text available.'}
              </Typography>
            </Paper>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This record has not been coded by any pipeline.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {normalized.blocks.map(renderBlock)}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ExpandedMatchDetailsModal
